import React, { useState, ChangeEvent, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  selectedField,
  openState,
  selectedRiskTab,
  titleState,
  selectedCommentId,
  selectedCommentText,
} from '../state/atoms';
import { selectedRiskAssessmentFilters } from '../../state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import useDialogState from '../hooks/useDialogState';
import { DialogActionButton, RiskCommentDialog, ScrollableBox } from './styles';
import { abyssColors } from '@/theme/colors';

import { useCommentsState } from '../hooks/useCommentsState';
import {
  AllCommentsForRiskAssessmentDocument,
  useUpdateCommentMutation,
} from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';

interface CommentDialogProps {
  open: boolean;
}

const CommentDialog: React.FC<CommentDialogProps> = ({ open }) => {
  const setIsOpen = useSetAtom(openState);
  const [selectedComment, setSelectedComment] = useAtom(selectedCommentId);
  const [currentCommentKey, setCurrentCommentKey] = useAtom(selectedCommentText);
  const key = useAtomValue(selectedField);
  const selectedTab = useAtomValue(selectedRiskTab);
  const selectedFilters = useAtomValue(selectedRiskAssessmentFilters);
  const title = useAtomValue(titleState);
  const structureId = useGetStructureId();
  const { resetDialogState } = useDialogState();
  const [comment, setComment] = useState<string>('');
  const { commentState, setCommentsState } = useCommentsState(selectedTab);
  const { showSnackBar } = useSnackBarMessage({});

  const [updateCommentMutation, { loading: updating }] = useUpdateCommentMutation();

  useEffect(() => {
    const commentExist = commentState?.comments.find(
      (commentItem) =>
        commentItem.key === key && commentItem.type === selectedTab && commentItem.text !== ''
    );
    if (commentExist?.text) {
      setComment(commentExist.text);
    } else {
      setComment('');
    }
  }, [key, selectedTab, commentState]);

  useEffect(() => {
    if (currentCommentKey) {
      setComment(currentCommentKey);
    }
  }, [currentCommentKey, open]);

  const handleCommentChange = (event: ChangeEvent<HTMLInputElement>) => {
    setComment(event.target.value);
  };

  const handleClose = () => {
    setIsOpen(false);
    setComment('');
    setSelectedComment(undefined);
    setCurrentCommentKey('');
  };

  const handleUpdateRemark = () => {
    if (!selectedComment) {
      return;
    }

    updateCommentMutation({
      variables: {
        input: {
          id: selectedComment,
          structureId,
          text: comment,
        },
      },
      refetchQueries: [AllCommentsForRiskAssessmentDocument],
    }).then(() => {
      showSnackBar('The comments have been updated successfully.', 'success');
      handleClose();
      resetDialogState();
    });
  };

  const handleAddRemark = () => {
    if (selectedComment) {
      handleUpdateRemark();
      return;
    }
    const newComment = {
      key,
      text: comment,
      type: selectedTab,
      title,
    };
    setCommentsState((previousState) => {
      if (previousState && previousState.comments.length > 0) {
        const existingCommentIndex = previousState.comments.findIndex(
          (c) => c.key === key && c.type === selectedTab
        );

        if (existingCommentIndex === -1) {
          return { ...previousState, comments: [...previousState.comments, newComment] };
        } else {
          const updatedComments = [...previousState.comments];
          updatedComments[existingCommentIndex] = {
            ...updatedComments[existingCommentIndex],
            ...newComment,
          };
          return { ...previousState, comments: updatedComments };
        }
      } else {
        return {
          structureId: structureId,
          collectionName: 'assembly',
          selectedFilters,
          comments: [newComment],
          updateAllAssemblies: false,
        };
      }
    });
    resetDialogState();
  };

  return (
    <RiskCommentDialog hideBackdrop open={open} onClose={handleClose} fullWidth>
      <DialogTitle>
        {selectedComment ? 'Edit Comment' : 'Add Comment'}
        <IconButton aria-label="close" onClick={handleClose} disableRipple>
          <CloseIcon sx={{ color: abyssColors.primary[600] }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: '16px' }}>
        <ScrollableBox>
          <TextField
            autoFocus
            id="note"
            type="text"
            fullWidth
            multiline
            variant="standard"
            value={comment}
            onChange={handleCommentChange}
            InputProps={{ disableUnderline: true }}
            sx={{
              '& .MuiInputBase-root': {
                padding: '1.6rem',
              },
            }}
          />
        </ScrollableBox>
      </DialogContent>
      <Divider />
      <DialogActions sx={{}}>
        <DialogActionButton onClick={handleClose}>Cancel</DialogActionButton>
        <DialogActionButton
          onClick={handleAddRemark}
          color="primary"
          variant="contained"
          contained
          disabled={!selectedComment && !comment.trim()}
          sx={{ gap: '1.2rem' }}
        >
          {!selectedComment && 'Add Comment'}
          {selectedComment && !updating && 'Update'}
          {selectedComment && updating && 'Updating'}
          {updating && <CircularProgress size={12} sx={{ color: abyssColors.neutral[50] }} />}
        </DialogActionButton>
      </DialogActions>
    </RiskCommentDialog>
  );
};

export default CommentDialog;
